<div class="component-wrapper">
  <div class="content-wrapper">
    <main>
      <h1>T-Test (abhängig)</h1>
      <p>
        Der <b> T-Test für abhängige Stichproben</b>, oder auch die dependent
        T-Test ist ein sehr einfacher Vergleich zweier Messzeitpunkte. Die
        Merkmale (abhängige Variable) müssen dabei ein metrisches Skalenniveau
        aufweisen. Die unabhängige Variable stellt hier der Zeitfaktor bzw. die
        beiden Zeitpunkte dar. Jedoch muss es sich nicht immer um reine
        Längsschnittanalysen handeln. Man könnte beispielsweise auch in einer
        Studie zu einem Zeitpunkt zwei metrische Variablen hinsichtlich ihrer
        Ausprägungen vergleichen.
      </p>
      <h2>Typ und Anwendung</h2>
      <p>
        <b>Unterschiedsanalyse</b> Gibt es einen Unterschied der Mittelwerte
        (arithmetische Mittel) zwischen zwei Zeitpunkten? Wichtig ist zudem,
        dass es sich hier um eine Längsschnittanalyse handelt. Während der
        T-Test für unabhängige Stichproben zwei Gruppen hinsichtlich eines
        Merkmals (Männlich-Weiblich hinsichtlich Klausurleistung) vergleicht,
        prüft der T-Test für abhängige Stichproben einen Unterscheid zwischen
        zwei Zeitpunkten oder zwei Variablen. Der eigentliche Unterschied liegt
        in der Versuchsperson/ Beobachtungseinheit. Hat jede Versuchsperson nur
        einen Wert und vergleiche ich am Ende zwei Gruppen, ist es eine
        unabhängige Stichprobe (Jede Versuchsperson kann nur Gruppe A oder
        Gruppe B sein). Hat jede Versuchsperson/ Beobachtungseinheit zwei Werte,
        liegt eine verbundene Stichprobe vor (Zeitpunkt/Variable A und B).
      </p>
      <h2>Beispiele</h2>
      <ul>
        <li>
          Welche Klausur ist besser ausgefallen? Neuropsychologie oder Statistik
          1?
        </li>
        <li></li>
        <li>
          Hat eine Therapie zu einer Verbesserung der Symptome (Laborparameter)
          geführt. Vorher vs. Nachher?
        </li>
        <li>
          Unterschieden sich ein Materialien hinsichtlich seiner Belastbarkeit
          in N/mm² nachdem ich es einem Stresstest unterzogen habe? Vorher vs.
          Nachher?
        </li>
        <li>
          Welches Produkt raten potenzielle Kunden besser? Produkt A oder B?
        </li>
      </ul>
      <h2>Voraussetzungen</h2>
      <ul>
        <li>Jede Person/Beobachtungseinheit muss zwei Werte aufweisen</li>
        <li>Die AV müssen metrisch sein</li>
        <li>
          Die AV müssen normalverteilt sein. Hier gibt es sicherlich mehrere
          Ansichten. Dem zentralen Grenzwertsatz entsprechend kann eine
          Verletzung dieser Voraussetzung bei Gruppengrößen &gt; 30 toleriert
          werden. Davon ist jedoch abzuraten, besonders bei sehr schiefen
          Verteilungen und den damit verbundenen Ausreißern
        </li>
        <li>
          Bei Verletzungen der Normalverteilung, besonders bei starken
          Ausreißern oder sehr schiefen Verteilungen bietet sich immer auch der
          Wilcoxon-Test für verbundene Stichproben an.
        </li>
      </ul>

      <h2>Beispiel SPSS</h2>
      <p>
        Gegeben sei ein einfacher Datensatz. 64 Studierende wurden hinsichtlich
        ihrer Neuropsychologie- und Statistikklausur befragt. Die Angabe
        erfolgte dabei in Prozent (Eine Schulnote stellt ein ordinales
        Skalenniveau dar und kann an dieser Stelle nicht verwendet werden!).
      </p>
      <img
        src="../../../assets/img/ttest_spss_abhängig.png"
        alt="T-Test Spss abhängig"
      />
      <p>
        Über Analysieren-&gt;Mittelwerte vergleichen-&gt;T-Test bei verbundenen
        Stichproben können wir unser Modell bauen. Wichtig sind an dieser Stelle
        auch die Tests der Voraussetzungen. Über Analysieren-&gt;Deskriptive
        Statistik-&gt;Explorative
        Datenanalyse-&gt;Diagramme-&gt;Normalverteilungsdiagramme mit Test
        können wir den Shapiro-Wilk-Test benutzen.
      </p>
      <p>
        Optional können wir uns das Modell auch selbst in der Syntax
        programmieren:
      </p>
      <div class="syntax" style="text-align: left; font-weight: bold">
        <span style="color: rgb(0, 132, 255)">T-TEST </span>
        <span style="color: rgb(194, 0, 0)">PAIRS</span
        >=Statistik_Resultat_in_Prozent
        <span style="color: rgb(255, 166, 0)">WITH</span>
        Bio_Resultat_in_Prozent (<span style="color: rgb(255, 166, 0)"
          >PAIRED</span
        >) <br />
        <span style="color: rgb(0, 128, 28)">/CRITERIA</span>=
        <span style="color: rgb(194, 0, 0)">CI</span> (.9500)
        <br />
        <span style="color: rgb(0, 128, 28)">/VARIABLES</span
        >=Resultat_in_Prozent <br />
        <span style="color: rgb(0, 128, 28)">/MISSING</span>=
        <span style="color: rgb(194, 0, 0)">ANALYSIS</span>. <br />
      </div>
      <p>Als Resultat zeigt sich folgendes Ergebnis:</p>
      <img
        src="../../../assets/img/ttest_spss_auswertung.png"
        alt="T-Test Spss auswertung"
      />

      <h2>Beispiel in R</h2>
      <p>
        Die Funktion „t.test“ ist in R sehr einfach aufgebaut. Nachdem wir
        unseren Datensatz eingelesen haben, können wir hier ganz einfach unseren
        Test bauen. Wenn wir den Datensatz df in der Funktion definiert haben,
        können wir an erster Stelle die abhängigen Variablen definieren, also
        die beiden Klausurleistungen. Wichtig ist an dieser Stelle das Argument
        „paired=TRUE“. Es bedeutet, dass es sich um eine verbundene Stichprobe
        handelt. Mit „shapiro.test“ können wir die Voraussetzung der
        Normalverteilung prüfen und ein Histogramm plotten.
      </p>
      <iframe
        width="100%"
        height="700"
        src="https://rdrr.io/snippets/embed/?code=%23Datensatz%20erstellen%0A%23%23Variablen%20erstellen%0A%0ABio_Resultat_in_Prozent%3C-c(23%2C23%2C45%2C87%2C67%2C56%2C34%2C45%2C56%2C45%2C40%2C40%2C40%2C45%2C45%2C50%2C50%2C50%2C50%2C50%2C50%2C50%2C50%2C56%2C56%2C56%2C56%2C60%2C60%2C67%2C67%2C67%2C67%2C70%2C70%2C70%2C70%2C73%2C73%2C73%2C73%2C76%2C76%2C76%2C56%2C76%2C85%2C67%2C76%2C56%2C87%2C56%2C45%2C45%2C91%2C45%2C65%2C76%2C87%2C67%2C76%2C56%2C76%2C56)%0AStatistik_Resultat_in_Prozent%3C-c(33%2C33%2C55%2C97%2C77%2C66%2C44%2C55%2C66%2C55%2C50%2C50%2C50%2C55%2C55%2C60%2C60%2C60%2C60%2C60%2C60%2C60%2C60%2C66%2C66%2C66%2C66%2C70%2C70%2C77%2C77%2C77%2C77%2C80%2C74%2C75%2C80%2C75%2C83%2C83%2C76%2C86%2C79%2C86%2C66%2C86%2C95%2C77%2C86%2C66%2C97%2C66%2C55%2C55%2C99%2C55%2C75%2C86%2C97%2C70%2C86%2C66%2C86%2C66)%0AStunden_gelernt%3C-c(21.00%2C18.00%2C19.00%2C23.00%2C25.00%2C30.00%2C4.00%2C16.00%2C10.00%2C25.00%2C5.00%2C10.00%2C19.00%2C28.00%2C10.00%2C27.00%2C21.00%2C18.00%2C19.00%2C23.00%2C25.00%2C30.00%2C4.00%2C16.00%2C10.00%2C25.00%2C5.00%2C10.00%2C19.00%2C28.00%2C10.00%2C27.00%2C21.00%2C18.00%2C19.00%2C23.00%2C25.00%2C30.00%2C4.00%2C16.00%2C10.00%2C25.00%2C5.00%2C10.00%2C19.00%2C28.00%2C10.00%2C27.00%2C21.00%2C18.00%2C19.00%2C23.00%2C25.00%2C30.00%2C4.00%2C16.00%2C10.00%2C25.00%2C5.00%2C10.00%2C19.00%2C28.00%2C10.00%2C27.00)%0AVersuch%3C-c(1.00%2C1.00%2C2.00%2C2.00%2C1.00%2C1.00%2C3.00%2C1.00%2C1.00%2C3.00%2C2.00%2C1.00%2C3.00%2C3.00%2C2.00%2C1.00%2C1.00%2C1.00%2C2.00%2C2.00%2C1.00%2C1.00%2C3.00%2C1.00%2C1.00%2C3.00%2C2.00%2C1.00%2C3.00%2C3.00%2C2.00%2C1.00%2C1.00%2C1.00%2C2.00%2C2.00%2C1.00%2C1.00%2C3.00%2C1.00%2C1.00%2C3.00%2C2.00%2C1.00%2C3.00%2C3.00%2C2.00%2C1.00%2C1.00%2C1.00%2C2.00%2C2.00%2C1.00%2C1.00%2C3.00%2C1.00%2C1.00%2C3.00%2C2.00%2C1.00%2C3.00%2C3.00%2C2.00%2C1.00)%0AGeschlecht%3C-c(.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00%2C1.00)%0A%0A%0A%0A%0A%23%23Zu%20einem%20Datensatz%20zusammenf%C3%BChren%0A%0Adf%3C-data.frame(Bio_Resultat_in_Prozent%2CStatistik_Resultat_in_Prozent%2CStunden_gelernt%2CVersuch%2CGeschlecht)%0A%0A%23%23%23UTest%20%0A%0At.test(Bio_Resultat_in_Prozent%2C%20Statistik_Resultat_in_Prozent%2C%20paired%20%3D%20TRUE%2C%20alternative%20%3D%20%22two.sided%22%2C%20data%3Ddf)%0A%0A%23%23%23Voraussetzungen%0A%0Ashapiro.test(df%24Bio_Resultat_in_Prozent)%0Ashapiro.test(df%24Statistik_Resultat_in_Prozent)%0A%0A%0A%23%23%23Plots%0A%0A%0Alibrary(ggplot2)%0A%0Aggplot(df%2C%20aes(x%3DBio_Resultat_in_Prozent))%20%2B%20%0A%20%20geom_histogram(binwidth%3D10%2C%20fill%3D'
%237aabd5'%2Ccolor%3D'white')%0Aggplot(df%2C%20aes(x%3DStatistik_Resultat_in_Prozent))%20%2B%20%0A%20%20geom_histogram(binwidth%3D10%2C%20fill%3D'%237aabd5'%2Ccolor%3D'white')"
        frameborder="0"
      ></iframe>
      <h2>Interpretation</h2>
      <p>
        Die Interpretation des T-Tests ist relativ einfach. Wir können uns
        zuerst die Voraussetzungen anschauen. Hier zeigt sich, dass die
        Normalverteilung (WStat(64)=.974, pStat=.119; s. AbbStat; WBio(64)=.970,
        pBio=.116; s. AbbBio) gegeben ist. Der Shapiro-Wilk-Test darf also nicht
        signifikant werden. Bei der Interpretation des eigentliches T-Tests
        zeigen sich, ein signifikanter Unterschied (t(63)=37.642, p&lt;.001).
        Dies deckt sich mit der deskriptiven Tendenz. Die Klausur in
        Biopsychologie (M=60.10, SD=15.25) ist wesentlich schlechter ausgefallen
        als die in Statistik (M=69.45, SD=14.83). Fragen, Probleme, Anmerkungen?
        Dann schreib und einfach und wir helfen dir sehr gerne weiter.
      </p>
      <div class="button" routerLink="/kostenloses-erstgepraech">
        <span>Kostenloses Erstgespräch</span>
      </div>
      <br />
      Dein Team von Hanseatic-Statistics

      <div class="quellen">
        Quellen: <br />
        Bortz, J. & Döring, N. (2016). Forschungsmethoden und Evaluation für
        Human- und Sozialwissenschaftler. 5. Auflage. Heidelberg: Springer.
        <br />
        Backhaus, K., Erichson, B., Plinke, W. & Weiber, R. (2016). Multivariate
        Analysemethoden. Eine anwendungsorientierte Einführung. 14. Auflage.
        Berlin: Springer. <br />
        Hirsig, R. (2006a). Statistische Methoden in den Sozialwissenschaften.
        Eine Einführung im Hinblick auf computergestützte Datenanalysen mit
        SPSS. Band I. 5. Auflage. Zürich: Seismo.
      </div>
    </main>
  </div>
</div>
